import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams, Link } from 'react-router-dom';
import { debounce } from "throttle-debounce";
import shortid from 'shortid';
import api from 'api';
import Util from 'utility';
import Loading from 'components/Loading/index';
import { __ } from 'locale';

import {
    showMessage,
    addFood,
} from 'actions/index';

const recipeItemCls = 'flex items-center border-t border-t-[#f0f0f0] py-[0.85em] hover:bg-[#fafafa] max-[639px]:items-start';
const pictureCls = 'basis-[48px] h-[48px] mr-[0.75em] bg-center bg-no-repeat bg-cover rounded-full cursor-pointer shrink-0 max-[639px]:basis-[56px] max-[639px]:h-[56px] max-[639px]:mt-[4px]';
const titleCls = 'flex-1 pl-[0.75em]';
const nutrDataCls = 'flex w-[560px] text-[0.85em] justify-center pr-[2em] max-[829px]:w-full max-[829px]:basis-full max-[639px]:flex-wrap [&>div]:inline-flex [&>div]:items-center [&>div]:justify-center [&>div]:basis-[calc((100%-100px)/5)] [&>div]:pr-[1em] [&>div>span]:hidden [&>div:last-child]:basis-[100px] max-[829px]:[&>div]:flex-wrap max-[829px]:[&>div]:justify-start max-[829px]:[&>div]:text-[0.9em] max-[829px]:[&>div>span]:block max-[829px]:[&>div>span]:basis-full max-[829px]:[&>div>span]:text-left max-[829px]:[&>div>span]:text-[0.8em] max-[829px]:[&>div>span]:text-[#555] max-[639px]:[&>div]:basis-[33.33%] max-[639px]:[&>div:last-child]:basis-[33.33%] max-[639px]:[&>div]:mb-[0.5em]';
const filtersItemCls = 'flex flex-wrap mb-[1em] [&>label]:basis-[calc(25%-1em)] [&>label]:min-w-[170px] [&>label]:mr-[1em] [&>label]:py-[2px] [&>label]:inline-flex [&>label]:items-center [&>label]:whitespace-nowrap [&>label]:cursor-pointer [&>label:hover]:text-[#26AA52] [&>label>input]:hidden [&>label>span]:inline-flex [&>label>span]:items-center [&>label>span]:justify-center [&>label>span]:text-[0.85em] [&>label>span]:w-[1.6em] [&>label>span]:h-[1.6em] [&>label>span]:bg-white [&>label>span]:border [&>label>span]:border-[#26AA52] [&>label>span]:text-white [&>label>span]:mr-[4px] [&>label>span]:rounded-[20px] [&>label>input:checked+span]:bg-[#26AA52]';
const addBtnCls = 'relative flex bg-[#4eb32d] text-white items-center justify-center w-[1.5em] h-[1.5em] text-[1.7em] rounded-full hover:bg-[#5ec33d] shrink-0 [&>span]:absolute [&>span]:right-[-0.5em] [&>span]:top-[-0.5em] [&>span]:text-[12px] [&>span]:flex [&>span]:items-center [&>span]:justify-center [&>span]:bg-white [&>span]:border [&>span]:border-[#4eb32d] [&>span]:text-[#4eb32d] [&>span]:w-[1.5em] [&>span]:h-[1.5em] [&>span]:rounded-full';
const blLink = (isActive) => `text-[1em] py-[0.2em] px-[0.3em] rounded border border-transparent ml-[5px] cursor-pointer no-underline ${isActive ? 'text-[#26AA52] bg-[#e9f5e5]' : 'bg-transparent text-black hover:border-[#ccc]'}`;

function FoodSearch({ isEmbedded, isDrinkRegime, onClick, groupId, mealId }) {
    const data = useSelector(state => state);
    const dispatch = useDispatch();
    const history = useHistory();
    const params = useParams();
    const { globals } = data;

    const [filter, setFilter] = useState(() => {
        let initialSources = ["0"];
        if (params.action === 'me') initialSources = ["1"];

        let initialFilter = {
            q: '',
            sources: initialSources,
            category: [],
            selection: 0,
            sortBy: '',
            sortOrder: '',
            lastSortIsCustom: false,
            customSortAttribute: '',
            viewMode: 'list',
            nutritionCalcMode: 'serving',
        };

        let fsLocal = localStorage.getItem('zof_foodsearch_settings');
        if (fsLocal) {
            fsLocal = JSON.parse(fsLocal);
            if (typeof fsLocal === 'object') {
                if (typeof fsLocal.q !== 'undefined') initialFilter.q = fsLocal.q;
                if (typeof fsLocal.sources !== 'undefined') initialFilter.sources = fsLocal.sources;
                if (typeof fsLocal.category !== 'undefined') initialFilter.category = fsLocal.category;
                if (typeof fsLocal.sortBy !== 'undefined') initialFilter.sortBy = fsLocal.sortBy;
                if (typeof fsLocal.sortOrder !== 'undefined') initialFilter.sortOrder = fsLocal.sortOrder;
                if (typeof fsLocal.lastSortIsCustom !== 'undefined') initialFilter.lastSortIsCustom = fsLocal.lastSortIsCustom;
                if (typeof fsLocal.customSortAttribute !== 'undefined') initialFilter.customSortAttribute = fsLocal.customSortAttribute;
                if (typeof fsLocal.viewMode !== 'undefined') initialFilter.viewMode = fsLocal.viewMode;
                if (typeof fsLocal.nutritionCalcMode !== 'undefined') initialFilter.nutritionCalcMode = fsLocal.nutritionCalcMode;
            }
        }

        return initialFilter;
    });
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const perPage = 30;
    const [loading, setLoading] = useState(false);
    const [foods, setFoods] = useState([]);
    const [selectedFoodIds, setSelectedFoodIds] = useState([]);
    const [addedFoodIds, setAddedFoodIds] = useState([]);

    const filterRef = useRef(filter);
    const pageRef = useRef(0);
    const loadFoodsRef = useRef(null);
    const loadFoodsDebounced = useRef(debounce(250, () => loadFoodsRef.current()));

    const loadFoods = () => {
        let profileId = Util.getActiveProfileId();
        if (typeof data.appModeData !== 'undefined') {
            if (typeof data.appModeData['profileId'] !== 'undefined') {
                profileId = data.appModeData['profileId'];
            }
        }
        api('foods2/list', { post: true, data: { page: pageRef.current, filter: filterRef.current, profileId: profileId }}).then(res => {
            setTotal(res.data.total);
            setFoods(res.data.foods);
            setLoading(false);
        });
    };
    loadFoodsRef.current = loadFoods;

    useEffect(() => {
        loadFoodsRef.current();
    }, []); // eslint-disable-line

    const saveLocalSettings = (f) => {
        localStorage.setItem('zof_foodsearch_settings', JSON.stringify(f));
    };

    const updateFilter = (e) => {
        const newFilter = { ...filterRef.current };
        let newPage = pageRef.current;
        const v = e.target.value;
        switch (e.target.name) {
            case 'q':
                newFilter.q = v;
                break;
            case 's':
                newFilter.sources = [v];
                newPage = 0;
                if (typeof isEmbedded === 'undefined') {
                    if (v == 0) {
                        history.push('/food');
                    } else {
                        history.push('/food/me');
                    }
                }
                break;
            case 'sel':
                newFilter.selection = v;
                newPage = 0;
                break;
            case 'c':
                if (newFilter.category.includes(v)) {
                    newFilter.category = newFilter.category.filter(item => item !== v);
                } else {
                    newFilter.category = [...newFilter.category, v];
                }
                newPage = 0;
                break;
            default:
                break;
        }

        saveLocalSettings(newFilter);
        filterRef.current = newFilter;
        pageRef.current = newPage;
        setFilter(newFilter);
        setPage(newPage);
        loadFoodsDebounced.current();
    };

    const updateSort = (e) => {
        const newFilter = { ...filterRef.current };
        let newSortBy = e.currentTarget.getAttribute('data-sort');
        if (newSortBy === 'custom') {
            if (e.currentTarget.getAttribute('data-change-order-only') === '1') {
                newSortBy = newFilter.sortBy;
                newFilter.sortOrder = newFilter.sortOrder === 'asc' ? 'desc' : 'asc';
            } else {
                newSortBy = e.target.value;
                newFilter.customSortAttribute = newSortBy;
                newFilter.sortBy = newSortBy;
                newFilter.sortOrder = 'desc';
            }
            newFilter.lastSortIsCustom = true;
        } else {
            newFilter.lastSortIsCustom = false;
            if (newSortBy === newFilter.sortBy) {
                switch (newFilter.sortOrder) {
                    case 'asc':
                        newFilter.sortOrder = 'desc';
                        break;
                    case 'desc':
                        newFilter.sortOrder = '';
                        newFilter.sortBy = '';
                        break;
                    default:
                        break;
                }
            } else {
                newFilter.sortBy = newSortBy;
                newFilter.sortOrder = 'asc';
            }
        }

        saveLocalSettings(newFilter);
        filterRef.current = newFilter;
        pageRef.current = 0;
        setFilter(newFilter);
        setPage(0);
        loadFoodsDebounced.current();
    };

    const setViewMode = (mode) => {
        const newFilter = { ...filterRef.current, viewMode: mode };
        filterRef.current = newFilter;
        setFilter(newFilter);
    };

    const setNutritionCalcMode = (mode) => {
        const newFilter = { ...filterRef.current, nutritionCalcMode: mode };
        filterRef.current = newFilter;
        setFilter(newFilter);
        loadFoodsDebounced.current();
    };

    const changePage = (e, p) => {
        setLoading(true);
        pageRef.current = p;
        setPage(p);
        loadFoodsRef.current();
    };

    const selectFood = (e, food) => {
        setSelectedFoodIds(prev => {
            let sf = prev.filter(id => id !== food.id);
            if (e.target.checked) sf = [...sf, food.id];
            return sf;
        });
    };

    const setSelectedAsFavorite = () => {
        const profileId = Util.getFirstProfileId();
        api('foods2/mass-set-favorite', { post: true, data: { profile_id: profileId, ids: selectedFoodIds.join(',') }}).then(() => {
            loadFoodsRef.current();
        });
    };

    const setSelectedAsUndesirable = () => {
        const profileId = Util.getFirstProfileId();
        api('foods2/mass-set-undesirable', { post: true, data: { profile_id: profileId, ids: selectedFoodIds.join(',') }}).then(() => {
            loadFoodsRef.current();
        });
    };

    const selectAll = () => {
        const ids = foods.map(f => f.id);
        setSelectedFoodIds(ids);
    };

    const selectNone = () => {
        setSelectedFoodIds([]);
    };

    const createDuplicate = (e, id) => {
        api('myfoods/duplicate/' + id, {post: true, data: {id: id}}).then(() => {
            dispatch(showMessage({ show: true, type: 'success', message: __('Duplikát byl vytvořen') }));
            loadFoodsRef.current();
        });
    };

    const handleFoodSelect = (e, food) => {
        const name = e.target.name;
        const url = name === 'favorite' ? 'food/set-favorite' : 'food/set-undesirable';
        const state = e.target.checked ? 1 : 0;

        setFoods(prev => prev.map(f => {
            if (f.id === food.id) {
                const updated = { ...f };
                if (state === 0) {
                    updated.u = 0;
                } else {
                    updated.u = name === 'favorite' ? 3 : 2;
                }
                return updated;
            }
            return f;
        }));

        api(url + '?state=' + state + '&id=' + food.id + '&profileId=' + Util.getActiveProfileId()).then(() => {});
    };

    const addToRecipe = (foodId, gId) => {
        let img_src;
        if (data.globals.myFoodIds.includes(foodId)) {
            img_src = 'https://www.zofapp.cz/data/zof/item/' + foodId + '.svg?r=' + Math.random();
        } else {
            img_src = 'https://www.zofapp.cz/data/zof/item/' + foodId + '.svg';
        }

        if (typeof gId === 'undefined') gId = '';
        const food = {
            id: shortid(),
            food_id: foodId,
            group_id: gId,
            image: img_src,
            title: data.globals.itemData[foodId]['title'],
            weight: data.globals.itemData[foodId]['weight'],
            is_drink: 0,
        };

        if (typeof mealId === 'string') {
            food.mealHash = mealId;
        }

        dispatch(addFood(food));

        setAddedFoodIds(prev => [...prev, foodId]);

        dispatch(showMessage({ show: true, type: 'success', message: __('Potravina vložena.') }));

        if (typeof mealId === 'string') {
            if (data.titles[data.activeMealHash] === '') {
                let firstFoodGroupTitle = '';
                for (let g in data.foodGroups[data.activeMealHash]) {
                    if (firstFoodGroupTitle === '') {
                        firstFoodGroupTitle = data.foodGroups[data.activeMealHash][g].title;
                    }
                }
                data.titles[data.activeMealHash] = firstFoodGroupTitle;
            }

            const dateParsed = new Date('2000-01-01');
            const dateUTC = new Date(Date.UTC(
                dateParsed.getFullYear(),
                dateParsed.getMonth(),
                dateParsed.getDate(),
                dateParsed.getHours(),
                dateParsed.getMinutes(),
                dateParsed.getSeconds()
            ));
            const dayTimestamp = Math.floor(dateUTC.getTime() / 1000);

            let saveData = {};
            saveData.meals = JSON.stringify(data.meals);
            saveData.groups = JSON.stringify(data.foodGroups);
            saveData.foods = JSON.stringify(data.foods);
            saveData.modifications = JSON.stringify(data.modifications);
            saveData.titles = JSON.stringify(data.titles);
            saveData.desciptions = JSON.stringify(data.desciptions);
            saveData.activities = JSON.stringify(data.activities);
            saveData.trainings = JSON.stringify(data.trainings);
            saveData.dayTimestamp = JSON.stringify(dayTimestamp);
            saveData.energyOutputSettings = JSON.stringify(data.energyOutputSettings);
            saveData = JSON.stringify(saveData);

            api('save/dayplan', {post: true, data: saveData}).then(() => {
                Util.doLoadDayEvaluation();
            });
        } else {
            Util.doLoadEvaluation();
        }
    };

    let sortSymbols = {};
    if (filter.sortBy !== '') {
        if (filter.sortOrder === 'asc') {
            sortSymbols[filter.sortBy] = <i className="fa fa-angle-up"></i>;
        } else {
            sortSymbols[filter.sortBy] = <i className="fa fa-angle-down"></i>;
        }
    }

    let sortSymbolCustom = false;
    if (filter.lastSortIsCustom) {
        if (filter.sortOrder === 'asc') {
            sortSymbolCustom = <a className="pl-[0.5em]" onClick={updateSort} data-sort="custom" data-change-order-only="1"><i className="fa fa-angle-up"></i></a>;
        } else {
            sortSymbolCustom = <a className="pl-[0.5em]" onClick={updateSort} data-sort="custom" data-change-order-only="1"><i className="fa fa-angle-down"></i></a>;
        }
    }

    const sortIgnore = [
        'weight', 'weight_total', 'amount', 'drink_amount', 'energy_output_kcal', 'energy_output_kj',
        'proteins', 'carbohydrates', 'fats', 'fats_omega3_dha_epa', 'energy_kcal', 'energy_kj', 'energy_balance_kcal', 'energy_balance_kj',
        'proteins_ratio', 'fats_saturated_unsaturated_ratio', 'fats_monounsaturated_polyunsaturated_ratio', 'fats_omega3_omega6_ratio',
        'fiber_ratio', 'calcium_magnesium_ratio', 'potassium_sodium_ratio', 'biotin',
    ];
    let customSortOptions = [];
    customSortOptions.push(
        <option value={''} key={''}>{__('Další')}...</option>
    );
    for (let attr in globals.attributes_sorted) {
        if (sortIgnore.includes(attr)) continue;
        customSortOptions.push(
            <option value={attr} key={attr}>{globals.attributes[attr]}</option>
        );
    }

    let amountText = 'Množství';
    let nutritionValuesText = 'Nutriční hodnoty';
    let portionText = 'porce';
    if (Util.getLang() === 'sk') {
        amountText = 'Množstvo';
        nutritionValuesText = 'Nutričné hodnoty';
        portionText = 'porcia';
    }

    const hdrNutrCls = 'flex text-[1em] justify-center pr-[2em] max-[829px]:hidden [&>div]:inline-flex [&>div]:items-center [&>div]:justify-center [&>div]:basis-[calc((100%-100px)/5)] [&>div]:pr-[1em] [&>div:last-child]:basis-[100px] [&>div>a]:underline [&>div>a]:text-[#737373] [&>div>a]:cursor-pointer';

    const foodsListHeader = (
        <div className="bg-[#fafafa] border-b border-[#eee] max-[829px]:hidden">
            <div className="flex items-center py-[0.25em] text-[0.75em] uppercase tracking-wide text-[#999] cursor-default [&>div]:py-[0.75em]">
                <div className="basis-[1.75em] pl-[5px]">{'\u00A0'}</div>
                <div className="basis-[40px] mr-[1em] flex items-center shrink-0">Foto</div>
                <div className="flex-1 pl-[1em]">
                    <h3 className="m-0 font-normal text-[#737373]">{__('Název')}</h3>
                </div>
                <div className={hdrNutrCls}>
                    <div>{amountText}</div>
                    <div><a onClick={updateSort} data-sort="energy_kcal">{__('Kalorie')} {sortSymbols['energy_kcal'] || false}</a></div>
                    <div><a onClick={updateSort} data-sort="proteins">{__('Bílkoviny')} {sortSymbols['proteins'] || false}</a></div>
                    <div><a onClick={updateSort} data-sort="carbohydrates">{__('Sacharidy')} {sortSymbols['carbohydrates'] || false}</a></div>
                    <div><a onClick={updateSort} data-sort="fats">{__('Tuky')} {sortSymbols['fats'] || false}</a></div>
                    <div>
                        <select name="custom_sort" onChange={updateSort} data-sort="custom" className="text-[11px] w-[6em]" value={filter.customSortAttribute}>
                            {customSortOptions}
                        </select>
                        {sortSymbolCustom}
                    </div>
                </div>
            </div>
        </div>
    );

    const optLabelBase = 'inline-block p-[1px] text-[1em] border-0 mr-[6px] cursor-pointer';

    let foodsList = [];
    if (foods.length > 0) {
        Object.keys(foods).map(key => {
            const food = foods[key];

            let nutrData = [];
            if (food.nutritionData !== '') {
                let customValue = parseFloat(food[filter.customSortAttribute]).toFixed(2);
                let customValueUnit = globals.attribute_units[filter.customSortAttribute];
                if (isNaN(customValue)) {
                    customValue = '';
                    customValueUnit = '';
                }

                if (['digestibility','acidobasicity','energy','thermic_effect'].includes(filter.customSortAttribute)) {
                    let ta = filter.customSortAttribute;
                    if (ta === 'energy') ta = 'energytendency';
                    if (ta === 'thermic_effect') ta = 'thermiceffect';

                    const keys = Object.keys(globals.evaluationTexts[ta]).map(a => a * 1);
                    const limitMin = Math.min(...keys);
                    const limitMax = Math.max(...keys);

                    let fval = Math.round(customValue);
                    if (fval < limitMin) fval = limitMin;
                    if (fval > limitMax) fval = limitMax;

                    customValue = globals.evaluationTexts[ta][fval];
                }

                food.weight = parseFloat(food.weight);

                nutrData.push(<div key="weight">{food.weight} {food.weight_unit}</div>);
                nutrData.push(<div key="energy">{Math.ceil(food.energy_kcal)} {globals.attribute_units['energy_kcal']}</div>);
                nutrData.push(<div key="proteins">{parseFloat(food.proteins).toFixed(2)} {globals.attribute_units['proteins']}</div>);
                nutrData.push(<div key="carbs">{parseFloat(food.carbohydrates).toFixed(2)} {globals.attribute_units['carbohydrates']}</div>);
                nutrData.push(<div key="fats">{parseFloat(food.fats).toFixed(2)} {globals.attribute_units['fats']}</div>);
                nutrData.push(<div key="custom">{customValue} {customValueUnit}</div>);
            }

            let foodOptions = false;
            if (typeof isEmbedded === 'undefined') {
                const isFav = food.u === 3;
                const isUnd = food.u === 2 || food.u === 1;
                foodOptions = (
                    <p className="my-[0.5em] p-0 flex items-center flex-wrap">
                        <input type="checkbox" className="hidden" checked={isFav} name="favorite" value={food.id} id={`fav_${food.id}`} onChange={(e) => handleFoodSelect(e, food)} />
                        <label title="Oblíbená potravina" className={`${optLabelBase} hover:text-[#0a0] ${isFav ? 'bg-[#0a0] text-white' : 'text-[#777]'}`} htmlFor={`fav_${food.id}`}>
                            <i className="fa fa-heart"></i>
                        </label>
                        <input type="checkbox" className="hidden" checked={isUnd} name="undesirable" value={food.id} id={`und_${food.id}`} onChange={(e) => handleFoodSelect(e, food)} />
                        <label title="Nežádoucí potravina" className={`${optLabelBase} hover:text-[#c00] ${isUnd ? 'bg-[#c00] text-white' : 'text-[#777]'}`} htmlFor={`und_${food.id}`}>
                            <i className="fa fa-ban"></i>
                        </label>
                        <Link title="Vytvořit kopii" className={`${optLabelBase} text-[#777] hover:text-[#26AA52]`} to={`/myfood/copy/${food.id}`}><i className="fa fa-plus"></i></Link>
                    </p>
                );
                if (food.ownership === 'user') {
                    foodOptions = (
                        <p className="my-[0.5em] p-0 flex items-center flex-wrap gap-[6px]">
                            <input type="checkbox" className="hidden" checked={food.u === 3} name="favorite" value={food.id} id={`fav_${food.id}`} onChange={(e) => handleFoodSelect(e, food)} />
                            <a className="underline cursor-pointer" onClick={(e) => createDuplicate(e, food.id)}>duplikovat</a>
                            <Link to={`/myfood/edit/${food.id}`} className="text-[#26AA52] no-underline hover:underline">upravit</Link>
                            <Link to={`/myfood/delete/${food.id}`}>smazat</Link>
                        </p>
                    );
                }
            }

            let undesirableInfo = false;
            if (!Util.isNutriKucharMode(data.appMode) && typeof data.appModeData !== 'undefined') {
                if (typeof data.appModeData.undesirableFoods !== 'undefined') {
                    if (data.appModeData.undesirableFoods.includes(food.id)) {
                        for (let profileId in data.globals.undesirableFoods) {
                            profileId = profileId * 1;
                            undesirableInfo = <p className="flex text-[0.85em] mt-[2px] text-[#f00] [&>span]:text-[0.85em] [&>span]:mr-[0.5em] [&>span]:font-bold [&>span]:uppercase"><span>Nevhodná potravina</span></p>;
                        }
                    }
                }
            }

            let alergenInfo = [];
            let watchAlergens = [];
            if (typeof data.appModeData.watchAlergens === 'object') {
                watchAlergens = data.appModeData.watchAlergens;
            }
            watchAlergens = watchAlergens.map(a => +a);

            let alergenList = [];
            if (typeof data.globals.foodAlergens[food.id] === 'object') {
                const alSorted = data.globals.foodAlergens[food.id].sort();
                alSorted.map(id => {
                    if (watchAlergens.includes(id * 1)) {
                        alergenList.push(<b key={`al-b-${id}`} className="text-[#f00]">{data.globals.itemAlergens[id]}</b>);
                        alergenList.push(<span key={`al-sep-${id}`}>, </span>);
                    } else {
                        alergenList.push(<span key={`al-${id}`}>{data.globals.itemAlergens[id]}</span>);
                        alergenList.push(<span key={`al-sep-${id}`}>, </span>);
                    }
                });
            }
            if (alergenList.length > 1) {
                alergenList.pop();
                alergenInfo = <p className="text-[0.8em] mt-[7px] mb-0">{__('Alergeny')}: {alergenList}</p>;
            }

            let foodImage = `https://www.zofapp.cz/data/zof/item/${food.id}.svg`;
            if (typeof food.custom_photo !== 'undefined' && food.custom_photo === '1') {
                foodImage = `https://www.zofapp.cz/data/hra/icon/png/${food.id}.png`;
            }

            let foodCheckBox = (
                <div className="basis-[1.75em] pl-[5px] shrink-0">
                    <input type="checkbox" checked={selectedFoodIds.includes(food.id)} onClick={(e) => selectFood(e, food)} />
                </div>
            );
            if (typeof isEmbedded !== 'undefined') foodCheckBox = false;

            let addToRecipeButton = false;
            if (typeof groupId === 'string') {
                if (addedFoodIds.includes(food.id)) {
                    addToRecipeButton = <a onClick={() => addToRecipe(food.id, groupId)} className={addBtnCls}><i className="fa fa-plus"></i><span><i className="fa fa-check"></i></span></a>;
                } else {
                    addToRecipeButton = <a onClick={() => addToRecipe(food.id, groupId)} className={addBtnCls}><i className="fa fa-plus"></i></a>;
                }
            }

            let foodTitle = <h3 className="m-0 text-[0.95em] font-semibold text-[#222]">{food.title}</h3>;
            if (food.has_description === 1) {
                foodTitle = <h3 className="m-0 text-[0.95em] font-semibold text-[#222] underline underline-offset-2 cursor-pointer hover:text-[#26AA52]" onClick={(e) => onClick(food.id, e)}>{food.title}</h3>;
            }

            foodsList.push(
                <div key={food.id} className={recipeItemCls}>
                    {foodCheckBox}
                    <div className={pictureCls} style={{ backgroundImage: `url(${foodImage})` }}></div>
                    <div className={titleCls}>
                        {foodTitle}
                        <p className="text-[11px] mt-[3px] mb-0 text-[#aaa] max-[829px]:hidden">{food.category_title}</p>
                        {alergenInfo}
                        {foodOptions}
                        {undesirableInfo}
                    </div>
                    {addToRecipeButton}
                    <div className={nutrDataCls}>
                        {nutrData}
                    </div>
                </div>
            );
        });
    }

    let pages = [];
    const numPages = Math.ceil(total / perPage);
    let pageMin = page - 5;
    let pageMax = page + 5;
    if (pageMin < 0) pageMin = 0;
    if (pageMax > numPages) pageMax = numPages;
    for (let p = pageMin; p < pageMax; p++) {
        if (p === page) {
            pages.push(<strong key={p}>{(p + 1)}</strong>);
        } else {
            pages.push(<a key={p} onClick={(e) => changePage(e, p)}>{(p + 1)}</a>);
        }
    }

    if (pageMin > 1) {
        pages = (
            <>
            <a onClick={(e) => changePage(e, 0)}>1</a>
            <span>...</span>
            {pages}
            </>
        );
    }

    if (pageMax < numPages) {
        pages = (
            <>
            {pages}
            <span>...</span>
            <a onClick={(e) => changePage(e, numPages - 1)}>{numPages}</a>
            </>
        );
    }

    let filter1 = false;

    let filter2 = (
        <span className="mr-[1.5em]">
            {nutritionValuesText}:
            <a className={blLink(filter.nutritionCalcMode === 'serving')} onClick={() => setNutritionCalcMode('serving')}>{portionText}</a>
            <a className={blLink(filter.nutritionCalcMode === '100g')} onClick={() => setNutritionCalcMode('100g')}>100 g</a>
        </span>
    );

    if (filter.viewMode === 'gallery') filter2 = false;

    let selectedFoodsOptions = false;
    const bulkBtnCls = 'ml-[0.5em] inline-block py-[0.3em] px-[0.7em] bg-[#888] text-white rounded-[3px] cursor-pointer hover:bg-[#666]';

    if (selectedFoodIds.length > 0) {
        selectedFoodsOptions = (
            <>
            Nastavit označené jako:
            <a className={bulkBtnCls} onClick={setSelectedAsFavorite}>Oblíbené</a>
            <a className={bulkBtnCls} onClick={setSelectedAsUndesirable}>Nežádoucí</a>
            </>
        );
    }

    let selectedFoodsSelectionOptions = (
        <p>Vybrat:
            <a className={bulkBtnCls} onClick={selectAll}>Vše</a>
            <a className={bulkBtnCls} onClick={selectNone}>Nic</a>
            {'\u00A0'}
            {selectedFoodsOptions}
        </p>
    );

    if (typeof isEmbedded !== 'undefined') {
        selectedFoodsSelectionOptions = false;
    }

    if (loading) foodsList = <Loading />;

    let filterSelection = (
        <>
        <div className="flex mb-[1em] last:mb-0 [&>p]:basis-[120px] [&>p]:pt-[6px] [&>div]:flex-1">
            <p>Výběr</p>
            <div className={filtersItemCls}>
                {Object.keys(globals.foodFilters.selection).map(key => {
                    const selectionTitle = globals.foodFilters.selection[key];
                    return <label key={key}><input type="radio" name="sel" value={key} checked={filter.selection == key} onChange={updateFilter} /><span></span> {selectionTitle}</label>;
                })}
            </div>
        </div>
        </>
    );

    let foodTabs = (
        <div className="flex items-center justify-center bg-[#eff1f7]">
            {Object.keys(globals.foodFilters.sources).map(key => {
                const isActive = filter.sources.includes(key);
                return <label key={key} className={`relative cursor-pointer top-[1px] z-[100] inline-block font-normal py-[0.9em] px-[2em] border border-[#e0e4eb] border-r-0 [&_input]:hidden first:rounded-tl-[10px] last:rounded-tr-[10px] last:border-r last:border-r-[#e0e4eb] ${isActive ? 'bg-white text-[#444] font-semibold border-b-white' : 'bg-[#ebebeb] text-[#404040] hover:bg-[#f1f1f1] hover:text-[#6c6c6c]'}`}><input type="checkbox" name="s" value={key} onChange={updateFilter} defaultChecked={isActive} /><span></span> {globals.foodFilters.sources[key]}</label>;
            })}
        </div>
    );

    let headerContent = (
        <div className="text-center py-[1.25em] pb-[0.75em] bg-[#eff1f7] relative">
            <h1>Potraviny</h1>
        </div>
    );

    if (Util.isLectureLikeMode(data.appMode)) {
        filterSelection = false;
        foodTabs = false;
        headerContent = false;
    }

    let placeholderText = 'Hledat v potravinách...';
    let filterText = 'filtr';
    let categoriesText = 'Kategorie';
    if (Util.getLang() === 'sk') {
        placeholderText = 'Hľadať v potravinách...';
        filterText = 'filter';
        categoriesText = 'Kategórie';
    }

    return (
        <div className="w-full flex flex-col">
            {headerContent}
            {foodTabs}
            <div className="flex flex-1 flex-col">
                <div className="w-full px-[1em] max-w-[1180px] mx-auto">
                    <input type="checkbox" id="cb-filters-advanced" className="peer/filters hidden" />
                    <div className="pt-[1em] pb-[0.5em] bg-white">
                        <div className="relative flex">
                            <i className="fa fa-search absolute left-[1em] top-1/2 -translate-y-1/2 text-[1.2em] text-[#bbb] pointer-events-none"></i>
                            <input className="flex-1 m-0 rounded-l-lg border-r-0 bg-white border border-[#e0e0e0] py-[0.85em] px-[1.25em] focus:outline-none focus:border-[#26AA52]" type="text" name="q" placeholder={placeholderText} value={filter.q} onChange={updateFilter} style={{paddingLeft:'3em',fontSize:'1.1em'}} />
                            <label className="flex items-center gap-[0.4em] text-[0.9em] py-[0.85em] px-[1.1em] cursor-pointer text-[#777] hover:bg-[#f5f5f5] hover:text-[#333]" htmlFor="cb-filters-advanced"><i className="fa fa-sliders-h text-[#26AA52]"></i> {filterText}</label>
                        </div>
                    </div>
                    <div className="bg-[#f8f9fc] border border-[#eaecf2] rounded-lg mx-auto mb-[0.75em] max-h-0 overflow-hidden transition-all duration-[150ms] ease-out text-[0.85em] peer-checked/filters:max-h-[1000px] peer-checked/filters:duration-500 [&_p]:font-bold">
                        <div className="p-[1em]">
                            <div className="flex mb-[1em] [&>p]:basis-[120px] [&>p]:pt-[6px] [&>div]:flex-1">
                                <p>{categoriesText}</p>
                                <div className={filtersItemCls}>
                                    {Object.keys(globals.foodFilters.categories).map(key => {
                                        const category = globals.foodFilters.categories[key];
                                        return <label key={key}><input type="checkbox" defaultChecked={filter.category.includes('' + category.id)} name="c" value={category.id} onChange={updateFilter} /><span></span> {category.title}</label>;
                                    })}
                                </div>
                            </div>
                            {filterSelection}
                            <div className="my-[1em] flex items-center justify-end max-[639px]:flex-wrap">
                                {filter1}
                                {filter2}
                            </div>
                        </div>
                    </div>
                    <div className="flex-1">
                        {foodsListHeader}
                        {foodsList}
                        {selectedFoodsSelectionOptions}
                        <div className="my-[1.5em] flex items-center justify-center gap-[3px] [&>a]:inline-flex [&>a]:items-center [&>a]:justify-center [&>a]:min-w-[2em] [&>a]:h-[2em] [&>a]:px-[0.3em] [&>a]:rounded-md [&>a]:no-underline [&>a]:text-[#555] [&>a]:cursor-pointer [&>a:hover]:bg-[#eee] [&>strong]:inline-flex [&>strong]:items-center [&>strong]:justify-center [&>strong]:min-w-[2em] [&>strong]:h-[2em] [&>strong]:px-[0.3em] [&>strong]:rounded-md [&>strong]:bg-[#26AA52] [&>strong]:text-white [&>span]:px-[0.25em] [&>span]:text-[#bbb]">{pages}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FoodSearch;
